import React, { useEffect, ReactNode, useState } from 'react';
import { HBChip } from '../../HBChip/HBChip';
import { ArrowDown16, DotsHorizontal16 } from '@honeybook/icons';
import HBMenu from '../../HBMenu/HBMenu';
import HBIconButton from '../../HBIconButton/HBIconButton';
import {
    TFilterFieldType,
    IFilterOption,
    IFilter,
    TFilterField
} from '../HBFilters.types';
import HBLinkButton from '../../HBLinkButton/HBLinkButton';
import { useFilters } from '../hooks/useFilters';
import styled from 'styled-components';
import { STRUCTURE } from '../utils/structure';
import PopoverBase, { StyledFilterItem } from './PopoverBase';
import { useEffectOnce } from 'react-use';

interface IFilterPopoverProps {
    filter: IFilter;
    children: ReactNode;
    enableInitialOpen?: boolean;
}

interface IFilterChipProps {
    filter: IFilter;
}

const FilterChip = ({ filter }: IFilterChipProps) => {
    const [label, setLabel] = useState('');
    const [title, setTitle] = useState('');

    useEffect(() => {
        const newChipLabel = getChipLabel(filter.field, filter.value);
        const fieldName = filter.field.label;
        setLabel(newChipLabel ? newChipLabel : fieldName);
        setTitle(newChipLabel && `${fieldName}:`);
    }, [filter.field, filter.value]);

    return (
        <StyledFilterItem>
            <HBChip
                testIdName="filter-chip"
                size={'small'}
                maxWidth="261px"
                selected={filter.isActive}
                theme="accent"
                title={title}
                label={label}
                suffixIcon={ArrowDown16}
                customStyles={{
                    title: {
                        flex: '0 0 auto',
                        maxWidth: '150px',
                        overflow: 'hidden'
                    },
                    label: {
                        flex: '1 1 0',
                        minWidth: 0,
                        overflow: 'hidden'
                    }
                }}
            />
        </StyledFilterItem>
    );
};

const FilterMenu = ({ filter }: { filter: IFilter }) => {
    const { deleteFilter, texts } = useFilters();

    return (
        <HBMenu
            withArrow={false}
            placement={'right'}
            align={'start'}
            offset={10}
            alignOffset={-10}
            preventClickPropogation
            testId="filter-popover-menu"
            target={
                <HBIconButton
                    size={'xs'}
                    testIdName="filter-popover-header-menu-icon"
                    onClick={() => {}}
                    label={'Menu'}
                    icon={DotsHorizontal16}
                />
            }
            targetAsChild
            menuItems={[
                {
                    title: texts.deleteFilter,
                    theme: 'destructive',
                    testId: 'delete',
                    type: 'item',
                    onClick: () => deleteFilter(filter.id)
                }
            ]}
        />
    );
};

const ClearSelectionButton = ({ filter }: { filter: IFilter }) => {
    const { clearSelection, texts } = useFilters();

    return (
        <ClearSelectionContainer>
            <HBLinkButton
                testIdName={`clear-selection-${filter.id}`}
                onClick={() => clearSelection(filter.id)}
                size="sm"
                fullWidth
                disabled={!filter.isActive}
                linkButtonTheme="neutral"
                alignment="start"
            >
                {texts.clearSelection}
            </HBLinkButton>
        </ClearSelectionContainer>
    );
};

const FilterPopover = ({ filter, children }: IFilterPopoverProps) => {
    const { texts } = useFilters();
    const title =
        filter.field.type === TFilterFieldType.MultiSelect
            ? texts.selectOptions
            : texts.selectOption;

    const [enableInitialOpen, setEnableInitialOpen] = useState(filter.isNew);
    const [disableAnimations, setDisableAnimations] =
        useState(enableInitialOpen);
    useEffectOnce(() => {
        setDisableAnimations(false);
        setEnableInitialOpen(false);
    });
    return (
        <PopoverBase
            target={<FilterChip filter={filter} />}
            title={title}
            menu={<FilterMenu filter={filter} />}
            testIdName={`filter-popover-${filter.id}`}
            enableInitialOpen={enableInitialOpen}
            disableAnimations={disableAnimations}
        >
            {children}
            <ClearSelectionButton filter={filter} />
        </PopoverBase>
    );
};

// -------------------------------- Styles -------------------------------- //
const ClearSelectionContainer = styled.div`
    padding: 10px ${STRUCTURE.popoverPadding};
    margin: ${STRUCTURE.itemMargin};
`;

// -------------------------------- Utils -------------------------------- //
const getChipLabel = (field: TFilterField, value: any): string => {
    if (!value) {
        return '';
    }

    if (field.type === TFilterFieldType.MultiSelect) {
        return value.map((v: IFilterOption) => v.label).join(', ');
    }

    return (value as IFilterOption).label;
};

export default FilterPopover;
